import { useCallback, useState } from 'react';

import { placedKey } from '../helpers/placedKey';
import { LineItemId, TransportUnitId } from '../types';
import { DropRequest } from './usePackingDragAndDrop';

export type ActiveDialog =
  | { kind: 'moveTo'; tuId: string | null; lineItemId: LineItemId }
  | { kind: 'editQuantity'; tuId: string; lineItemId: LineItemId }
  | { kind: 'bulkDrop'; request: DropRequest }
  | null;

export const usePackingDialogs = (params: {
  placedContentByKey: Map<string, { tuId: TransportUnitId; lineItemId: LineItemId; quantity: number }>;
  remainingByLineItem: Map<LineItemId, number>;
}) => {
  const { placedContentByKey, remainingByLineItem } = params;
  const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);

  const openMoveTo = useCallback(
    (tuId: string | null, lineItemId: LineItemId) => setActiveDialog({ kind: 'moveTo', tuId, lineItemId }),
    [],
  );
  const openEditQuantity = useCallback(
    (tuId: string, lineItemId: LineItemId) => setActiveDialog({ kind: 'editQuantity', tuId, lineItemId }),
    [],
  );
  const openBulkDrop = useCallback((request: DropRequest) => setActiveDialog({ kind: 'bulkDrop', request }), []);
  const close = useCallback(() => setActiveDialog(null), []);

  // How much of the item is available to move: a placed item is capped at the quantity in its
  // source unit; a source item (tuId === null) at its unplaced remainder.
  const moveToMax =
    activeDialog?.kind === 'moveTo'
      ? activeDialog.tuId
        ? (placedContentByKey.get(placedKey(activeDialog.tuId, activeDialog.lineItemId))?.quantity ?? 0)
        : (remainingByLineItem.get(activeDialog.lineItemId) ?? 0)
      : 0;

  return {
    activeDialog,
    openMoveTo,
    openEditQuantity,
    openBulkDrop,
    close,
    moveToMax,
  };
};
